/**
 * The Call Off Management results table — one row per call-off, with the
 * bulk-select checkbox, the viewer's columns in the viewer's order, the draft
 * row actions, and (CR-7782) an expand control that shows the call-off's items
 * under its row.
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. Presentational apart from which rows are expanded: rows,
 * columns, selection and actions all arrive as props.
 *
 * @link CallOffManagementTab for the consumer.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CallOff } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../ui/table';
import {
  Loader2, SendHorizonal, XCircle,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight,
} from 'lucide-react';
import { orderingFieldFor, type CallOffColumn } from './callOffColumnConfig';
import CallOffListCell from './CallOffListCell';
import CallOffExpandedItems from './CallOffExpandedItems';

interface CallOffListTableProps {
  callOffs: CallOff[];
  loading: boolean;
  isCustomer: boolean;
  /** The detail status whose rows can be bulk-selected by this viewer. */
  selectableStatus: string;
  selectableCount: number;
  allSelectableChecked: boolean;
  selectedIds: Set<number>;
  onToggleSelectAll: () => void;
  onToggleSelect: (id: number) => void;
  onRowClick: (id: number) => void;
  onSend: (id: number) => void;
  onCancel: (id: number) => void;
  /** CR-7782: the columns showing, in the viewer's order. */
  columns: readonly CallOffColumn[];
  /**
   * CR-7732: a haulier collects FROM the supplier, so the supplier is the
   * party worth naming on its list — the customer is the same on every row.
   */
  isHaulier?: boolean;
  /** CR-7732: the active `ordering` value, e.g. 'pickup_date' or '-created_at'. */
  ordering?: string;
  /** Ask the server for a different order. Absent = headers are not sortable. */
  onSort?: (ordering: string) => void;
}

export default function CallOffListTable({
  callOffs, loading, isCustomer, selectableStatus, selectableCount,
  allSelectableChecked, selectedIds, onToggleSelectAll, onToggleSelect,
  onRowClick, onSend, onCancel, columns, isHaulier = false,
  ordering, onSort,
}: CallOffListTableProps) {
  const { formatDate } = useFormatters();
  const partyIsSupplier = isCustomer || isHaulier;
  // CR-7782: which rows show their items. A new page, filter, sort or refresh
  // hands over a new set of rows, and every row closes with it — an open row
  // never outlives the list it was opened on. Reset while rendering, not in an
  // effect, so a refetch never mounts (and fetches for) a row about to close.
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [rowsExpandedFor, setRowsExpandedFor] = useState(callOffs);
  if (rowsExpandedFor !== callOffs) {
    setRowsExpandedFor(callOffs);
    setExpandedIds(new Set());
  }

  // The list's visible width. A list wider than the screen scrolls sideways,
  // and an expanded row spanning all of it would push the quantities and
  // weights off-screen; sized to this and pinned left, it stays in view.
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleWidth, setVisibleWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setVisibleWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Chevron + checkbox + the chosen columns + the customer's Actions.
  const expandedColSpan = 2 + columns.length + (isCustomer ? 1 : 0);

  // CR-7732: one header cell that knows how to sort, so every column asks the
  // same way. Sorting is asked of the SERVER: the list is paginated, so
  // reordering the rows on screen would say "sorted" while the row that
  // belongs first sat on page three. A column with no ordering field behind it
  // stays a plain header rather than looking clickable and doing nothing.
  const SortHeader = ({ column }: { column: CallOffColumn }) => {
    const alignRight = column.align === 'right';
    const field = onSort ? orderingFieldFor(column.id, partyIsSupplier) : null;
    if (!field) {
      return <TableHead className={alignRight ? 'text-right' : ''}>{column.label}</TableHead>;
    }
    const active = ordering === field || ordering === `-${field}`;
    const descending = ordering === `-${field}`;
    return (
      <TableHead
        className={`cursor-pointer select-none hover:bg-muted ${alignRight ? 'text-right' : ''}`}
        aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
        onClick={() => onSort?.(active && !descending ? `-${field}` : field)}
      >
        <div className={`flex items-center gap-1 ${alignRight ? 'justify-end' : ''}`}>
          <span>{column.label}</span>
          {!active && <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 shrink-0" />}
          {active && !descending && <ArrowUp className="h-3 w-3 text-primary shrink-0" />}
          {active && descending && <ArrowDown className="h-3 w-3 text-primary shrink-0" />}
        </div>
      </TableHead>
    );
  };

  return (
    <Card>
      <CardContent className="p-0" ref={containerRef}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : callOffs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No call-offs found.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" aria-label="Expand" />
                <TableHead className="w-10">
                  {selectableCount > 0 && (
                    <Checkbox
                      checked={allSelectableChecked}
                      onCheckedChange={onToggleSelectAll}
                    />
                  )}
                </TableHead>
                {columns.map((column) => <SortHeader key={column.id} column={column} />)}
                {/* CR-7732: only the customer has actions here. For the
                    supplier and the haulier the cell only ever read "Click to
                    view", restating what clicking the row already does. */}
                {isCustomer && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {callOffs.map((co) => {
                const isSelectable = co.detail_status === selectableStatus;
                const isExpanded = expandedIds.has(co.id);

                return (
                  <Fragment key={co.id}>
                    <TableRow
                      className={`cursor-pointer ${isExpanded ? 'border-b-0 bg-muted/30' : ''}`}
                      onClick={() => onRowClick(co.id)}
                    >
                      {/* CR-7782: the items, under the row, without opening it. */}
                      <TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} items for ${co.reference}`}
                          onClick={() => toggleExpand(co.id)}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </TableCell>

                      {/* Checkbox */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {isSelectable && (
                          <Checkbox
                            checked={selectedIds.has(co.id)}
                            onCheckedChange={() => onToggleSelect(co.id)}
                          />
                        )}
                      </TableCell>

                      {columns.map((column) => (
                        <CallOffListCell
                          key={column.id}
                          columnId={column.id}
                          callOff={co}
                          partyIsSupplier={partyIsSupplier}
                          isCustomer={isCustomer}
                          formatDate={formatDate}
                        />
                      ))}

                      {/* Actions */}
                      {isCustomer && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {co.detail_status === 'draft' ? (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-green-700 border-green-300 hover:bg-green-50"
                              title="Send to Supplier"
                              onClick={() => onSend(co.id)}
                            >
                              <SendHorizonal className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-red-700 border-red-300 hover:bg-red-50"
                              title="Cancel"
                              onClick={() => onCancel(co.id)}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Click to view</span>
                        )}
                      </TableCell>
                      )}
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={expandedColSpan} className="px-6 pb-4 pt-0">
                          {/* As wide as the visible list (less this cell's
                              padding), pinned to its left edge. */}
                          <div
                            className="sticky left-6 overflow-x-auto rounded-md border bg-background p-3"
                            style={visibleWidth ? { width: Math.max(visibleWidth - 48, 320) } : undefined}
                          >
                            <CallOffExpandedItems callOffId={co.id} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
